import React, { useEffect, useRef, useState, useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useStory } from '../../context/StoryContext';

/**
 * InfiniteMarquee — Endless scrolling marquee for ecosystem logos/integrations
 * Used in GoingMerry (ecosystem breadth) and Contact (network breadth)
 * Respects prefers-reduced-motion, pauses on hover, uses narrative color tokens
 *
 * Importers: src/pages/GoingMerryPage.jsx (The Ecosystem beat), src/pages/ContactPage.jsx (The Network beat)
 * Affected API: Exports InfiniteMarquee, MarqueeItem, MarqueeGroup components
 * Data schema: Props accept items (array of { src, alt, href, label }), speed (pixels/sec), direction ('left'|'right'), gap (number), pauseOnHover (boolean), colorScheme ('ice'|'goingmerry'|'ramsey'|'cortex'), height (number), beatId (string), threshold (number), rootMargin (string), showGlow (boolean)
 * User instruction: "see this website is good... but the brand needs some kinda storytelling UI/UX... right? dont u think ? use ur marketing skills and make up a plan"
 */
const InfiniteMarquee = ({
  items = [],
  speed = 50,
  direction = 'left',
  gap = 32,
  pauseOnHover = true,
  colorScheme = 'goingmerry',
  height = 80,
  className = '',
  style,
  beatId,
  threshold = 0.1,
  rootMargin = '0px',
  showGlow = true,
  ...props
}) => {
  const { reducedMotion: contextReducedMotion, isBeatActive } = useStory();
  const framerReducedMotion = useReducedMotion();
  const reducedMotion = contextReducedMotion || framerReducedMotion;
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const [trackWidth, setTrackWidth] = useState(0);
  const isCurrentBeat = beatId ? isBeatActive(beatId) : false;

  
  // Default ecosystem items for GoingMerry
  const defaultItems = useMemo(() => [
    { label: 'GGUF', href: 'https://github.com/ggerganov/ggml', color: 'var(--goingmerry-primary)' },
    { label: 'llama.cpp', href: 'https://github.com/ggerganov/llama.cpp', color: 'var(--goingmerry-secondary)' },
    { label: 'Metal', href: 'https://developer.apple.com/metal/', color: '#FF9F0A' },
    { label: 'CUDA', href: 'https://developer.nvidia.com/cuda-zone', color: '#76B900' },
    { label: 'Vulkan', href: 'https://www.vulkan.org/', color: '#E61B24' },
    { label: 'ONNX Runtime', href: 'https://onnxruntime.ai/', color: '#0078D4' },
    { label: 'TensorRT', href: 'https://developer.nvidia.com/tensorrt', color: '#76B900' },
    { label: 'Core ML', href: 'https://developer.apple.com/machine-learning/', color: '#FF9F0A' },
    { label: 'Hugging Face', href: 'https://huggingface.co/', color: '#FFD21E' },
    { label: 'Ollama', href: 'https://ollama.ai/', color: '#000000' },
    { label: 'LM Studio', href: 'https://lmstudio.ai/', color: '#6366F1' },
    { label: 'Transformers.js', href: 'https://huggingface.co/docs/transformers.js', color: '#FF6B35' },
  ], []);

  const activeItems = items.length > 0 ? items : defaultItems;

  // IntersectionObserver for scroll-triggered animation
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  // Measure track width and start animation
  useEffect(() => {
    if (!isVisible || reducedMotion) return;

    const track = trackRef.current;
    if (!track) return;

    // Wait for layout
    const measure = () => {
      const width = track.scrollWidth;
      if (width > 0) {
        setTrackWidth(width);
      } else {
        requestAnimationFrame(measure);
      }
    };
    measure();

    // Animation loop using CSS animation for smooth infinite scroll
    const trackEl = trackRef.current;
    if (trackEl) {
      const duration = trackWidth / speed; // seconds
      trackEl.style.animationDuration = `${duration}s`;
      trackEl.style.animationPlayState = 'running';
    }
  }, [isVisible, reducedMotion, speed, trackWidth]);

  // Pause on hover
  useEffect(() => {
    if (!pauseOnHover || reducedMotion) return;

    const container = containerRef.current;
    if (!container) return;

    const handleMouseEnter = () => {
      setIsHovered(true);
      const track = trackRef.current;
      if (track) track.style.animationPlayState = 'paused';
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      const track = trackRef.current;
      if (track) track.style.animationPlayState = 'running';
    };

    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [pauseOnHover, reducedMotion]);

  // Build track content (duplicated for seamless loop)
  const trackContent = useMemo(() => {
    const renderCopy = (copy) => activeItems.map((item, index) => (
      <MarqueeItem
        key={`${copy}-${index}`}
        item={item}
        height={height}
        gap={gap}
        colorScheme={colorScheme}
      />
    ));
    return [...renderCopy("a"), ...renderCopy("b")]; // Duplicate for seamless loop; each copy needs its own keys
  }, [activeItems, height, gap, colorScheme]);

  if (reducedMotion) {
    return (
      <div
        ref={containerRef}
        className={`infinite-marquee ${isCurrentBeat ? 'beat-active' : ''} ${className}`}
        style={{
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
          ...style,
        }}
        {...props}
        aria-label="Ecosystem integrations"
      >
        <div
          ref={trackRef}
          className="marquee-track"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap,
            padding: '1rem 0',
            width: 'max-content',
          }}
        >
          {trackContent}
        </div>
        {showGlow && (
          <>
            <div className="marquee-glow marquee-glow-left" style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '100px',
              background: `linear-gradient(to right, var(--carbon, #0A0A0F), transparent)`,
              pointerEvents: 'none',
              zIndex: 1,
            }} />
            <div className="marquee-glow marquee-glow-right" style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '100px',
              background: `linear-gradient(to left, var(--carbon, #0A0A0F), transparent)`,
              pointerEvents: 'none',
              zIndex: 1,
            }} />
          </>
        )}
      </div>
    );
  }

  return (
    <motion.div
      ref={containerRef}
      className={`infinite-marquee ${isCurrentBeat ? 'beat-active' : ''} ${className}`}
      style={{
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        ...style,
      }}
      {...props}
      initial={{ opacity: 0 }}
      animate={{ opacity: isVisible ? 1 : 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      aria-label="Ecosystem integrations"
      onMouseEnter={() => pauseOnHover && setIsHovered(true)}
      onMouseLeave={() => pauseOnHover && setIsHovered(false)}
    >
      <div
        ref={trackRef}
        className="marquee-track"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap,
          padding: '1rem 0',
          width: 'max-content',
          animation: `marquee-${direction} ${trackWidth / speed}s linear infinite`,
          animationPlayState: isHovered ? 'paused' : 'running',
          willChange: 'transform',
        }}
      >
        {trackContent}
      </div>
      {showGlow && (
        <>
          <div className="marquee-glow marquee-glow-left" style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '100px',
            background: `linear-gradient(to right, var(--carbon, #0A0A0F), transparent)`,
            pointerEvents: 'none',
            zIndex: 1,
          }} />
          <div className="marquee-glow marquee-glow-right" style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '100px',
            background: `linear-gradient(to left, var(--carbon, #0A0A0F), transparent)`,
            pointerEvents: 'none',
            zIndex: 1,
          }} />
        </>
      )}
      <style>{`
        @keyframes marquee-left {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        @keyframes marquee-right {
          from { transform: translateX(-50%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </motion.div>
  );
};

/**
 * MarqueeItem — Individual item in the marquee
 */
const MarqueeItem = ({ item, height = 80, gap = 32, colorScheme = 'goingmerry' }) => {

  // If item has src (image), render as image; otherwise render as styled label
  if (item.src) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        aria-label={item.alt || item.label}
      >
        <img
          src={item.src}
          alt={item.alt || item.label}
          style={{
            height: `${height * 0.7}px`,
            width: 'auto',
            maxWidth: '120px',
            filter: 'grayscale(100%) opacity(0.7)',
            transition: 'filter 300ms var(--ease-out)',
          }}
          onMouseEnter={(e) => {
            e.target.style.filter = 'grayscale(0%) opacity(1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.filter = 'grayscale(100%) opacity(0.7)';
          }}
          loading="lazy"
        />
      </a>
    );
  }

  // Render as styled pill/badge
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.5rem 1rem',
        borderRadius: '9999px',
        background: 'var(--glass-2)',
        border: `1px solid ${item.color || 'var(--border-default)'}`,
        color: item.color || 'var(--text-primary)',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
        textDecoration: 'none',
        transition: 'all 300ms var(--ease-out)',
      }}
    >
      {item.label}
    </span>
  );
};

/**
 * MarqueeGroup — Groups multiple marquees with different speeds for parallax effect
 */
export const MarqueeGroup = ({
  groups = [], // [{ items, speed, direction, height, colorScheme }]
  className = '',
  style,
  beatId,
  ...props
}) => {
  const { reducedMotion: contextReducedMotion } = useStory();
  const framerReducedMotion = useReducedMotion();
  const reducedMotion = contextReducedMotion || framerReducedMotion;

  return (
    <div
      className={`marquee-group ${className}`}
      style={{ display: 'flex', flexDirection: 'column', gap: '1rem', ...style }}
      {...props}
      aria-hidden={reducedMotion}
    >
      {groups.map((group, index) => (
        <InfiniteMarquee
          key={index}
          items={group.items}
          speed={group.speed || 50}
          direction={group.direction || (index % 2 === 0 ? 'left' : 'right')}
          height={group.height || 60}
          colorScheme={group.colorScheme || 'goingmerry'}
          beatId={beatId ? `${beatId}-group-${index}` : undefined}
          pauseOnHover={true}
          showGlow={index === 0}
        />
      ))}
    </div>
  );
};

export { InfiniteMarquee, MarqueeItem };
export default InfiniteMarquee;